import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import {
  getBlueprintPerformanceDetails,
  type BlueprintPerformanceDetails,
  BLUEPRINT_QUALITY_ALERT_THRESHOLD,
  BLUEPRINT_SLA_ALERT_THRESHOLD,
} from '../../../../../services/blueprintPerformanceService';
import type { ProcessBlueprint } from '../../../../types/processBlueprint';
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface BlueprintPerformanceTabProps {
  blueprint: ProcessBlueprint;
  organizationId: string;
}

type LoadState = 'idle' | 'loading' | 'success' | 'error';

export function BlueprintPerformanceTab({ blueprint, organizationId }: BlueprintPerformanceTabProps) {
  const [state, setState] = useState<{ status: LoadState; data: BlueprintPerformanceDetails | null; error?: string }>({
    status: 'idle',
    data: null,
  });

  const hasTemplate = Boolean(blueprint.generatedTemplateId);

  const loadPerformance = useCallback(() => {
    if (!organizationId || !blueprint.id || !hasTemplate) {
      setState({ status: hasTemplate ? 'idle' : 'success', data: null });
      return;
    }

    let cancelled = false;
    setState({ status: 'loading', data: null });
    getBlueprintPerformanceDetails({
      organizationId,
      blueprintId: blueprint.id,
      templateId: blueprint.generatedTemplateId ?? undefined,
    })
      .then((data) => {
        if (cancelled) return;
        setState({ status: 'success', data });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setState({ status: 'error', data: null, error: err.message });
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId, blueprint.id, blueprint.generatedTemplateId, hasTemplate]);

  useEffect(() => {
    const cleanup = loadPerformance();
    return () => {
      if (typeof cleanup === 'function') cleanup();
    };
  }, [loadPerformance]);

  if (!hasTemplate) {
    return (
      <div className="rounded-md border border-dashed border-gray-300 p-4 text-sm text-gray-600">
        Gere um workflow template a partir deste blueprint para acompanhar o desempenho operacional.
      </div>
    );
  }

  if (state.status === 'loading') {
    return <PerformanceSkeleton />;
  }

  if (state.status === 'error') {
    return (
      <div className="space-y-3 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800/40 dark:bg-red-500/10 dark:text-red-300">
        <p>{state.error ?? 'Falha ao carregar métricas de desempenho.'}</p>
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-md border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 dark:border-red-800/40 dark:text-red-300 dark:hover:bg-red-500/15"
          onClick={() => loadPerformance()}
        >
          <RefreshCw size={14} />
          Tentar novamente
        </button>
      </div>
    );
  }

  const metrics = state.data?.metrics;
  const trend = state.data?.trend ?? [];

  const avgLeadTimeHours = metrics?.avgExecutionTime ? (metrics.avgExecutionTime / 60).toFixed(1) : '—';
  const slaCompliance = metrics?.slaBreachRate != null ? (100 - metrics.slaBreachRate).toFixed(1) : '—';
  const isCritical =
    (metrics?.reprocessRate ?? 0) > BLUEPRINT_QUALITY_ALERT_THRESHOLD ||
    (metrics?.slaBreachRate != null && 100 - metrics.slaBreachRate < BLUEPRINT_SLA_ALERT_THRESHOLD);

  return (
    <div className="space-y-4">
      {isCritical && (
        <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/40 dark:bg-amber-500/10 dark:text-amber-300">
          Desvio estrutural detectado na performance do blueprint. Recomenda-se revisão do padrão vigente.
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
        <MetricCard label="Total execuções" value={metrics?.totalExecutions ?? '—'} />
        <MetricCard label="Reprocessos" value={metrics?.totalReprocessed ?? '—'} helper={`${(metrics?.reprocessRate ?? 0).toFixed(1)}%`} />
        <MetricCard label="Tempo médio" value={avgLeadTimeHours !== '—' ? `${avgLeadTimeHours}h` : '—'} />
        <MetricCard label="SLA médio" value={slaCompliance !== '—' ? `${slaCompliance}%` : '—'} helper="Conformidade" />
      </div>

      <div className="rounded border border-base-200 bg-base-100 p-4">
        <div className="mb-3 flex items-center justify-between text-sm">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Tendência (30 dias)</p>
            <p className="font-semibold text-gray-900">Taxa de reprocesso</p>
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-sm border border-gray-200 px-3 py-1 text-xs"
            onClick={() => loadPerformance()}
          >
            <RefreshCw size={12} />
            Atualizar
          </button>
        </div>
        <div className="h-48">
          {trend.length === 0 ? (
            <div className="flex h-full items-center justify-center text-xs text-gray-500">
              Sem execuções registradas nos últimos 30 dias.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={192}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} />
                <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} labelFormatter={(label) => `Dia ${label}`} />
                <Line type="monotone" dataKey="reprocessRate" stroke="#dc2626" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {state.data?.versions.length ? (
        <div className="rounded border border-base-200 bg-base-100 p-4">
          <div className="mb-3">
            <p className="text-xs uppercase tracking-wide text-gray-500">Comparativo por versão</p>
            <p className="font-semibold text-gray-900">Desempenho consolidado das versões publicadas</p>
          </div>
          <div className="space-y-2">
            {state.data.versions.map((version) => (
              <div
                key={version.blueprint_version_id}
                className="grid gap-3 rounded border border-base-200 bg-base-50 px-4 py-3 text-xs md:grid-cols-[100px_1fr_90px_90px_1fr]"
              >
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-gray-500">Versão</p>
                  <p className="font-semibold text-gray-900">{version.version_semver}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-gray-500">Gargalo principal</p>
                  <p className="font-semibold text-gray-900">{version.gargalo_principal ?? 'Sem gargalo dominante'}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-gray-500">Execuções</p>
                  <p className="font-semibold text-gray-900">{version.total_execucoes}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-gray-500">SLA</p>
                  <p className="font-semibold text-gray-900">{version.sla_percentual != null ? `${version.sla_percentual}%` : '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-gray-500">Lead / Reprocesso</p>
                  <p className="font-semibold text-gray-900">
                    {version.lead_time_medio != null ? `${(version.lead_time_medio / 60).toFixed(1)}h` : '—'} · {version.taxa_reprocesso.toFixed(1)}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MetricCard({ label, value, helper }: { label: string; value: string | number; helper?: string }) {
  return (
    <div className="rounded border border-base-200 bg-base-50 px-4 py-3">
      <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-xl font-semibold text-gray-900">{value}</p>
      {helper && <p className="text-[11px] text-gray-500">{helper}</p>}
    </div>
  );
}

function PerformanceSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      {Array.from({ length: 6 }).map((_, idx) => (
        <div key={idx} className="h-4 rounded bg-base-200" />
      ))}
      <div className="h-48 rounded bg-base-200" />
    </div>
  );
}
